import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { RevealLines } from "./Reveal";
import { BRAND } from "../../data";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-white pt-[68px] md:pt-[76px]"
    >
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-76px)] py-16">
          {/* Left: large hero logo centerpiece */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.9, ease: EASE }}
            className="order-1 flex items-center justify-center"
          >
            <div className="relative flex items-center justify-center">
              {/* soft brand glow (CSS only, no image) */}
              <div className="pointer-events-none absolute inset-0 -m-10 rounded-full bg-brand-orange/10 blur-3xl" />
              <div className="pointer-events-none absolute inset-0 -m-4 rounded-full bg-brand-golden/10 blur-2xl" />
              <motion.img
                src={BRAND.logo}
                alt="SocialImpactCLE logo — a starburst of diverse hands radiating from a glowing center"
                data-testid="hero-logo"
                animate={{ y: [0, -14, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10 w-[74vw] max-w-[260px] sm:max-w-[360px] lg:max-w-[480px] aspect-square rounded-3xl bg-white object-contain p-5 sm:p-6 shadow-[0_40px_90px_-30px_rgba(232,98,26,0.5)]"
              />
            </div>
          </motion.div>

          {/* Right: headline, subheadline, CTA */}
          <div className="order-2 relative z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.8 }}
              className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-brand-teal mb-6 flex items-center gap-3"
            >
              <span className="inline-block h-px w-10 bg-brand-teal" />
              Born in Cleveland · Built for the world
            </motion.p>

            <h1 className="font-display font-black tracking-tighter text-charcoal leading-[0.92] text-[2.75rem] sm:text-[3.75rem] lg:text-[5rem]">
              <RevealLines
                lines={["Empowering Communities.", "Creating Impact."]}
                delay={0.3}
              />
              <span className="block overflow-hidden mt-1">
                <motion.span
                  className="block text-brand-orange"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
                >
                  For Cleveland &amp; Beyond.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
              className="mt-8 max-w-xl text-lg md:text-xl text-charcoal/70 leading-relaxed"
            >
              A Cleveland-born platform driving social impact through
              community-powered programs.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#programs"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-12px_rgba(232,98,26,0.6)]"
              >
                Explore Our Programs
                <ArrowDownRight
                  size={20}
                  className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
                />
              </a>
              <a
                href="#about"
                className="inline-flex items-center gap-2 rounded-full border border-charcoal/20 px-8 py-4 text-base font-semibold text-charcoal transition-all duration-300 hover:border-charcoal hover:-translate-y-1"
              >
                Our Mission
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
